// Workspace de un Anexo vivo: capturas | ledger | vista previa.
//
// Estructura portada de RecepcionDigital.jsx / Produccion.jsx: el contenedor es
// el único dueño del estado transversal y del autoguardado; los paneles no
// hablan con la base por su cuenta salvo el typeahead del catálogo.
//
// El encabezado es net-new: el HeaderStrip de Recepción Digital es una franja
// de identidad de solo lectura, no un encabezado de página con acciones.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, FileCheck2, Loader2, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import IntakeFeedPanel from './IntakeFeedPanel';
import ProduccionPanel from '../../components/anexo/ProduccionPanel';
import DgaPreviewPanel from './DgaPreviewPanel';
import {
  aplanarItems,
  buildAnexoPdfInput,
  itemIncompleto,
  ordenarPaquetes,
  shortAnexoId,
} from './anexoToPdfInput';
import { renderAnexoPdf } from '../../utils/generateAnexoPDF';

const ROLES_PERMITIDOS = ['admin', 'manager']; // 'bodega' se agrega post-expo
const PULSO_AUTOGUARDADO = 3000;

// Preferencia persistente entre sesiones — por eso localStorage y no
// sessionStorage, que del lado cliente guarda identidad de una sola sesión.
const PREVIEW_KEY = 'desarrollos_preview_collapsed';

const leerPreviewColapsado = () => {
  try {
    return localStorage.getItem(PREVIEW_KEY) === 'true';
  } catch {
    return false;
  }
};

const CHIP_PERFIL = {
  activado: 'bg-green-50 text-green-700 border-green-200',
  'pendiente_de_activación': 'bg-slate-100 text-slate-600 border-slate-200',
  'revisión_requerida': 'bg-amber-50 text-amber-700 border-amber-200',
};

const ETIQUETA_PERFIL = {
  activado: 'Activado',
  'pendiente_de_activación': 'Pendiente',
  'revisión_requerida': 'Revisión',
};

const last4 = (t) => (t || '').replace(/\D/g, '').slice(-4);

const Bloqueo = ({ titulo, cuerpo, onVolver }) => (
  <div className="min-h-screen bg-[#f8fafc] p-8">
    <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-10 text-center">
      <p className="text-sm font-bold text-slate-900">{titulo}</p>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">{cuerpo}</p>
      <button
        type="button"
        onClick={onVolver}
        className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft size={13} /> Anexos activos
      </button>
    </div>
  </div>
);

const DesarrollosAnexoWorkspace = ({ anexoId, onBack, onDenied }) => {
  const [gate, setGate] = useState('checking'); // checking | denied | notfound | perfil | ok
  const [anexo, setAnexo] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [ultimoPaqueteId, setUltimoPaqueteId] = useState(null);
  const [estadoGuardado, setEstadoGuardado] = useState('inactivo');
  const [ultimoAgregadoId, setUltimoAgregadoId] = useState(null);
  const [beat, setBeat] = useState(0);
  const [previewColapsado, setPreviewColapsadoState] = useState(leerPreviewColapsado);
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorGenerar, setErrorGenerar] = useState('');

  const setPreviewColapsado = useCallback((valor) => {
    setPreviewColapsadoState(valor);
    try {
      localStorage.setItem(PREVIEW_KEY, String(valor));
    } catch {
      // Safari en modo privado; la preferencia vive solo en memoria.
    }
  }, []);

  const sucios_items = useRef(new Set());
  const sucios_packages = useRef(new Set());
  const timerRef = useRef(null);
  const paquetesRef = useRef([]);
  paquetesRef.current = paquetes;

  // ── Carga inicial ────────────────────────────────────────────────────────
  const cargarEventos = useCallback(async () => {
    const { data, error } = await supabase
      .from('desarrollos_intake_events')
      .select('*')
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .eq('anexo_id', anexoId)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[Desarrollos/workspace] error cargando capturas', error);
      return;
    }
    setEventos(data || []);
  }, [anexoId]);

  // Reemplaza la vieja carga plana: dos lecturas y el agrupado en JS, para que
  // el estado quede anidado (packages[].items[]) igual que lo consumen el
  // ledger y el adaptador del PDF.
  const cargarPaquetes = useCallback(async () => {
    const { data: pkgs, error } = await supabase
      .from('desarrollos_anexo_packages')
      .select('id, nombre, sort_order, created_at, updated_at')
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .eq('anexo_id', anexoId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[Desarrollos/workspace] error cargando paquetes', error);
      return [];
    }

    const ids = (pkgs || []).map((p) => p.id);
    let filas = [];
    if (ids.length > 0) {
      const { data: its, error: itemsError } = await supabase
        .from('desarrollos_anexo_items')
        .select('*, customs_catalog:customs_catalog_id(id, descripcion)')
        .eq('tenant_id', DESARROLLOS_TENANT_ID)
        .in('package_id', ids)
        .order('created_at', { ascending: true });
      if (itemsError) {
        console.error('[Desarrollos/workspace] error cargando artículos', itemsError);
      } else {
        filas = its || [];
      }
    }

    const porPaquete = {};
    filas.forEach((it) => {
      (porPaquete[it.package_id] = porPaquete[it.package_id] || []).push(it);
    });

    const anidados = (pkgs || []).map((p) => ({ ...p, items: porPaquete[p.id] || [] }));
    setPaquetes(anidados);
    setBeat((b) => b + 1);
    return anidados;
  }, [anexoId]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) {
        if (!cancelado && onDenied) onDenied();
        return;
      }
      const { data: staff } = await supabase
        .from('staff_members')
        .select('role, tenant_id')
        .eq('id', session.user.id)
        .maybeSingle();
      if (cancelado) return;
      if (
        !staff ||
        !ROLES_PERMITIDOS.includes(staff.role) ||
        staff.tenant_id !== DESARROLLOS_TENANT_ID
      ) {
        setGate('denied');
        return;
      }

      const { data: a } = await supabase
        .from('desarrollos_anexos')
        .select('id, client_id, status, created_at, updated_at, anexo_pdf_url, completed_at')
        .eq('tenant_id', DESARROLLOS_TENANT_ID)
        .eq('id', anexoId)
        .maybeSingle();
      if (cancelado) return;
      if (!a || a.status !== 'live') {
        setGate('notfound');
        return;
      }
      setAnexo(a);

      const [c, p] = await Promise.all([
        supabase
          .from('customers')
          .select('id, full_name, phone_number')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('id', a.client_id)
          .maybeSingle(),
        supabase
          .from('desarrollos_perfil_aduanero')
          .select('client_id, codigo_gestor, codigo_aduana, nombre_gestor, no_registro, estado')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('client_id', a.client_id)
          .maybeSingle(),
      ]);
      if (cancelado) return;
      setCliente(c.data || null);
      setPerfil(p.data || null);

      if (p.data?.estado !== 'activado') {
        setGate('perfil');
        return;
      }

      await Promise.all([cargarEventos(), cargarPaquetes()]);
      if (!cancelado) setGate('ok');
    })();
    return () => {
      cancelado = true;
    };
  }, [anexoId, onDenied, cargarEventos, cargarPaquetes]);

  // ── Realtime ─────────────────────────────────────────────────────────────
  // Las capturas nuevas del cliente entran solas: este es el momento de la demo.
  useEffect(() => {
    if (gate !== 'ok') return undefined;
    const channel = supabase
      .channel(`desarrollos_workspace_${anexoId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'desarrollos_intake_events',
          filter: `anexo_id=eq.${anexoId}`,
        },
        () => cargarEventos()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'desarrollos_anexo_items',
          filter: `anexo_id=eq.${anexoId}`,
        },
        () => {
          // Refetch ingenuo: si el cambio lo hicimos nosotros, el resultado es
          // el mismo que ya está en pantalla.
          if (sucios_items.current.size === 0) cargarPaquetes();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'desarrollos_anexo_packages',
          filter: `anexo_id=eq.${anexoId}`,
        },
        () => {
          // Un paquete cambió: se recargan paquetes e ítems para reconstruir la
          // agrupación completa.
          if (sucios_packages.current.size === 0) {
            cargarPaquetes();
          }
        }
      )
      .subscribe((status) => console.log('[Desarrollos/workspace] canal realtime:', status));

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gate, anexoId, cargarEventos, cargarPaquetes]);

  // ── Autoguardado silencioso a los 3 s ────────────────────────────────────
  const programarGuardado = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const ids = Array.from(sucios_items.current);
      const idsPaquetes = Array.from(sucios_packages.current);
      if (ids.length === 0 && idsPaquetes.length === 0) return;
      setEstadoGuardado('guardando');
      try {
        // Nombres de paquete — mismo pulso, misma máquina de estado.
        for (const pid of idsPaquetes) {
          const pkg = paquetesRef.current.find((p) => p.id === pid);
          if (!pkg) continue;
          const { error } = await supabase
            .from('desarrollos_anexo_packages')
            .update({ nombre: pkg.nombre || '' })
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .eq('id', pid);
          if (error) throw error;
        }
        sucios_packages.current.clear();

        for (const id of ids) {
          const it = paquetesRef.current.flatMap((p) => p.items || []).find((x) => x.id === id);
          if (!it) continue;
          const cantidad = Number(it.cantidad) || 0;
          const fobUnit = Number(it.fob_unitario_usd) || 0;
          const cipUnit = it.cip_unitario_usd === null || it.cip_unitario_usd === undefined
            ? null
            : Number(it.cip_unitario_usd);
          const { error } = await supabase
            .from('desarrollos_anexo_items')
            .update({
              descripcion: it.descripcion || '',
              cantidad,
              unidad_medida: it.unidad_medida || 'UNIDAD',
              fob_unitario_usd: fobUnit,
              fob_total_usd: cantidad * fobUnit,
              cip_unitario_usd: cipUnit,
              cip_total_usd: cipUnit === null ? null : cantidad * cipUnit,
              customs_catalog_id: it.customs_catalog_id || null,
              manual_override: !!it.manual_override,
            })
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .eq('id', id);
          if (error) throw error;
        }
        sucios_items.current.clear();
        setEstadoGuardado('guardado');
        setBeat((b) => b + 1);
        setTimeout(() => setEstadoGuardado((e) => (e === 'guardado' ? 'inactivo' : e)), 2500);
      } catch (err) {
        console.error('[Desarrollos/workspace] fallo el autoguardado', err);
        setEstadoGuardado('fallo');
      }
    }, PULSO_AUTOGUARDADO);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const onChangeItem = useCallback(
    (id, cambios) => {
      setPaquetes((prev) =>
        prev.map((p) => ({
          ...p,
          items: (p.items || []).map((it) => (it.id === id ? { ...it, ...cambios } : it)),
        }))
      );
      sucios_items.current.add(id);
      programarGuardado();
    },
    [programarGuardado]
  );

  const insertarItems = useCallback(
    async (filas) => {
      const { data, error } = await supabase
        .from('desarrollos_anexo_items')
        .insert(filas)
        .select('*');
      if (error) {
        console.error('[Desarrollos/workspace] error insertando artículos', error);
        setEstadoGuardado('fallo');
        return;
      }
      // Las filas nuevas entran en su paquete; el orden dentro del grupo sigue
      // siendo created_at ASC, que es el orden en que vienen del INSERT.
      const nuevas = data || [];
      setPaquetes((prev) =>
        prev.map((p) => {
          const suyas = nuevas.filter((it) => it.package_id === p.id);
          return suyas.length ? { ...p, items: [...(p.items || []), ...suyas] } : p;
        })
      );
      if (nuevas.length) setUltimoAgregadoId(nuevas[nuevas.length - 1].id);
      setBeat((b) => b + 1);
    },
    []
  );

  const onChangeNombrePaquete = useCallback(
    (paqueteId, nombre) => {
      setPaquetes((prev) => prev.map((p) => (p.id === paqueteId ? { ...p, nombre } : p)));
      sucios_packages.current.add(paqueteId);
      programarGuardado();
    },
    [programarGuardado]
  );

  // package_id es NOT NULL, así que el paquete tiene que existir antes.
  const asegurarPaquete = useCallback(async () => {
    const actuales = paquetesRef.current;
    if (actuales.length > 0) {
      return ordenarPaquetes(actuales)[0].id;
    }
    const { data, error } = await supabase
      .from('desarrollos_anexo_packages')
      .insert([
        {
          anexo_id: anexoId,
          tenant_id: DESARROLLOS_TENANT_ID,
          nombre: cliente?.full_name || 'Paquete 1',
          sort_order: 0,
        },
      ])
      .select('id, nombre, sort_order, created_at')
      .single();
    if (error) throw error;
    setPaquetes((prev) => [...prev, { ...data, items: [] }]);
    return data.id;
  }, [anexoId, cliente]);

  const onAgregarPaquete = useCallback(async () => {
    const siguiente =
      paquetesRef.current.reduce((max, p) => Math.max(max, p.sort_order ?? 0), -1) + 1;
    const { data, error } = await supabase
      .from('desarrollos_anexo_packages')
      .insert([
        {
          anexo_id: anexoId,
          tenant_id: DESARROLLOS_TENANT_ID,
          nombre: '',
          sort_order: siguiente,
        },
      ])
      .select('id, nombre, sort_order, created_at')
      .single();
    if (error) {
      console.error('[Desarrollos/workspace] error creando el paquete', error);
      setEstadoGuardado('fallo');
      return;
    }
    setPaquetes((prev) => [...prev, { ...data, items: [] }]);
    setUltimoPaqueteId(data.id);
    setBeat((b) => b + 1);
  }, [anexoId]);

  const onAgregarItem = useCallback(
    async (paqueteId) => {
      const destino = paqueteId || (await asegurarPaquete());
      insertarItems([
        {
          anexo_id: anexoId,
          tenant_id: DESARROLLOS_TENANT_ID,
          package_id: destino,
          descripcion: '',
          cantidad: 1,
          unidad_medida: 'UNIDAD',
          fob_unitario_usd: 0,
          fob_total_usd: 0,
          manual_override: true,
        },
      ]);
    },
    [anexoId, insertarItems, asegurarPaquete]
  );

  // Promover: si la captura trae extracted_fields.items los usa; si no, deja
  // una fila en blanco ligada al evento para que el operador la llene.
  const onPromover = useCallback(
    async (evento) => {
      // Los ítems promovidos caen en el PRIMER paquete (menor sort_order).
      // Si el Anexo todavía no tiene ninguno, se crea con el nombre del cliente.
      let destino;
      try {
        destino = await asegurarPaquete();
      } catch (err) {
        console.error('[Desarrollos/workspace] no se pudo preparar el paquete', err);
        setEstadoGuardado('fallo');
        return;
      }

      const extraidos = evento.extracted_fields?.items;
      if (Array.isArray(extraidos) && extraidos.length > 0) {
        insertarItems(
          extraidos.map((x) => {
            const cantidad = Number(x.cantidad) || 1;
            const fobUnit = Number(x.fob_unitario_usd) || 0;
            return {
              anexo_id: anexoId,
              tenant_id: DESARROLLOS_TENANT_ID,
              package_id: destino,
              source_event_id: evento.id,
              descripcion: String(x.descripcion || '').slice(0, 60),
              cantidad,
              unidad_medida: x.unidad_medida || 'UNIDAD',
              fob_unitario_usd: fobUnit,
              fob_total_usd: cantidad * fobUnit,
              customs_catalog_id: x.catalog_match_id || null,
              manual_override: !x.catalog_match_id,
            };
          })
        );
        return;
      }
      insertarItems([
        {
          anexo_id: anexoId,
          tenant_id: DESARROLLOS_TENANT_ID,
          package_id: destino,
          source_event_id: evento.id,
          descripcion: '',
          cantidad: 1,
          unidad_medida: 'UNIDAD',
          fob_unitario_usd: 0,
          fob_total_usd: 0,
          manual_override: true,
        },
      ]);
    },
    [anexoId, insertarItems, asegurarPaquete]
  );

  const onQuitarItem = useCallback(async (id) => {
    // .select() y comprobar longitud: un DELETE bloqueado por RLS no lanza error.
    const { data, error } = await supabase
      .from('desarrollos_anexo_items')
      .delete()
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .eq('id', id)
      .select('id');
    if (error || !data || data.length === 0) {
      console.error('[Desarrollos/workspace] no se pudo eliminar el artículo', error);
      setEstadoGuardado('fallo');
      return;
    }
    sucios_items.current.delete(id);
    setPaquetes((prev) =>
      prev.map((p) => ({ ...p, items: (p.items || []).filter((it) => it.id !== id) }))
    );
    setBeat((b) => b + 1);
  }, []);

  // Lista plana de artículos — totales, validación y el PDF la derivan de acá.
  const todosLosItems = useMemo(() => aplanarItems(paquetes), [paquetes]);

  // ── Generar Anexo ────────────────────────────────────────────────────────
  const motivoBloqueo = useMemo(() => {
    if (todosLosItems.length === 0) return 'Agregá al menos un artículo antes de generar el Anexo.';
    if (todosLosItems.some(itemIncompleto)) {
      return 'Hay artículos sin descripción, cantidad o valor FOB.';
    }
    return '';
  }, [todosLosItems]);

  const generarAnexo = useCallback(async () => {
    if (motivoBloqueo || generando) return;
    setGenerando(true);
    setErrorGenerar('');
    try {
      const bytes = await renderAnexoPdf(
        buildAnexoPdfInput({ anexo, cliente, perfil, packages: paquetes }),
        { drawTaxColumn: true, logLabel: `anexo:${anexoId}` }
      );

      const ruta = `${anexo.client_id}/${anexoId}/anexo-${Date.now()}.pdf`;
      const { error: upError } = await supabase.storage
        .from('desarrollos-anexos')
        .upload(ruta, new Blob([bytes], { type: 'application/pdf' }), {
          contentType: 'application/pdf',
          upsert: false,
        });
      if (upError) throw upError;

      const { data: firmada } = await supabase.storage
        .from('desarrollos-anexos')
        .createSignedUrl(ruta, 60 * 60 * 24 * 7);

      const ahora = new Date().toISOString();
      const { data: actualizado, error: updError } = await supabase
        .from('desarrollos_anexos')
        .update({
          status: 'completed',
          completed_at: ahora,
          anexo_pdf_generated_at: ahora,
          anexo_pdf_url: `desarrollos-anexos/${ruta}`,
        })
        .eq('tenant_id', DESARROLLOS_TENANT_ID)
        .eq('id', anexoId)
        .select('id');
      if (updError || !actualizado || actualizado.length === 0) {
        throw updError || new Error('No se pudo marcar el Anexo como completado.');
      }

      setResultado({ url: firmada?.signedUrl || null });
    } catch (err) {
      console.error('[Desarrollos/workspace] error generando el Anexo', err);
      setErrorGenerar('No pudimos generar el Anexo. Revisá tu conexión y volvé a intentar.');
    } finally {
      setGenerando(false);
    }
  }, [motivoBloqueo, generando, anexo, cliente, perfil, paquetes, anexoId]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (gate === 'checking') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-8">
        <div className="mx-auto grid max-w-[1600px] grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-96 animate-pulse rounded-lg border border-slate-200 bg-white" />
          ))}
        </div>
      </div>
    );
  }

  if (gate === 'denied') {
    return (
      <Bloqueo
        titulo="Sin permisos para Desarrollos"
        cuerpo="Tu usuario no tiene rol de operador en este tenant."
        onVolver={onBack}
      />
    );
  }

  if (gate === 'notfound') {
    return (
      <Bloqueo
        titulo="Anexo no encontrado"
        cuerpo="Puede que ya se haya completado o que pertenezca a otro tenant."
        onVolver={onBack}
      />
    );
  }

  if (gate === 'perfil') {
    return (
      <Bloqueo
        titulo="El Perfil de este cliente no está activado"
        cuerpo={`Antes de armar un Anexo, el Perfil Aduanero de ${
          cliente?.full_name || 'este cliente'
        } debe quedar activado.`}
        onVolver={onBack}
      />
    );
  }

  const estadoPerfil = perfil?.estado;

  return (
    <div className="flex h-screen min-h-0 flex-col bg-[#f8fafc]">
      {/* Encabezado — net-new */}
      <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-5">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Anexos activos
          </button>
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-bold text-slate-900">
              {cliente?.full_name || anexo?.client_id}
            </span>
            <span className="shrink-0 text-xs text-slate-400">
              •••• {last4(cliente?.phone_number)}
            </span>
            <span
              className={`shrink-0 rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${
                CHIP_PERFIL[estadoPerfil] || CHIP_PERFIL['pendiente_de_activación']
              }`}
            >
              {ETIQUETA_PERFIL[estadoPerfil] || 'Pendiente'}
            </span>
            <span className="shrink-0 font-mono text-[10px] text-slate-400">
              {shortAnexoId(anexoId)}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {errorGenerar && <span className="text-xs text-red-600">{errorGenerar}</span>}
          <button
            type="button"
            onClick={generarAnexo}
            disabled={!!motivoBloqueo || generando}
            title={motivoBloqueo || 'Generar el PDF final y cerrar el Anexo'}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {generando ? <Loader2 size={15} className="animate-spin" /> : <FileCheck2 size={15} />}
            {generando ? 'Generando…' : 'Generar Anexo'}
          </button>
        </div>
      </header>

      {/* Tres paneles. Al colapsar la vista previa queda un riel de 32px y el
          ledger se queda con el ancho que sobra. */}
      <div
        className="grid min-h-0 flex-1 gap-3 p-3 transition-[grid-template-columns] duration-200 ease-out"
        style={{
          gridTemplateColumns: previewColapsado ? '28% 1fr 32px' : '28% 36% 36%',
        }}
      >
        <IntakeFeedPanel eventos={eventos} onPromover={onPromover} />
        <ProduccionPanel
          paquetes={paquetes}
          estadoGuardado={estadoGuardado}
          ultimoAgregadoId={ultimoAgregadoId}
          ultimoPaqueteId={ultimoPaqueteId}
          onChangeNombrePaquete={onChangeNombrePaquete}
          onChangeItem={onChangeItem}
          onQuitarItem={onQuitarItem}
          onAgregarItem={onAgregarItem}
          onAgregarPaquete={onAgregarPaquete}
        />
        <DgaPreviewPanel
          anexo={anexo}
          cliente={cliente}
          perfil={perfil}
          packages={paquetes}
          beat={beat}
          colapsado={previewColapsado}
          onToggle={setPreviewColapsado}
        />
      </div>

      {/* Modal terminal */}
      {resultado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start justify-between">
              <p className="text-sm font-bold text-slate-900">Anexo generado</p>
              <button type="button" onClick={onBack} aria-label="Cerrar">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              El Anexo quedó completado y ya no aparece en la cola de activos.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              {resultado.url && (
                <a
                  href={resultado.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-h-[44px] items-center justify-center rounded-lg bg-slate-900 text-sm font-semibold text-white hover:opacity-90"
                >
                  Abrir PDF
                </a>
              )}
              <button
                type="button"
                onClick={onBack}
                className="min-h-[44px] rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Volver a Anexos activos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesarrollosAnexoWorkspace;
